import { mergeProps, useObjectRef } from '@react-aria/utils';
import type {
  AriaLabelingProps,
  DOMProps,
  FocusableProps,
  HoverEvents,
  LinkDOMProps,
  PressEvents,
} from '@react-types/shared';
import { cva } from 'class-variance-authority';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { useButton, useFocusRing, useHover } from 'react-aria';
import { Provider } from 'react-aria-components';

import { ArrowUpRightIcon } from '@infinex/ui-icons';
import { IconContext } from '@infinex/ui-icons/context';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useStyleProps } from '@infinex/ui-styles';
import { UNICODE_CHARS } from '@infinex/ui-utils/constants';
import { toDataAttributes } from '@infinex/ui-utils/dom';
import { isReactText } from '@infinex/ui-utils/react';

import { Link } from './Link';

import { Text } from '../content';

export type CommonTextLinkProps = {
  /** The content of the element. */
  children: ReactNode;
  /**
   * The visual prominence of the link.
   * @default default
   */
  prominence?: 'high' | 'default' | 'low';
  /** Whether the element is disabled. */
  isDisabled?: boolean;
  /**
   * Optionally provide a title for the link. Useful in cases where you expect
   * the content to be truncated.
   */
  title?: string;
} & AriaLabelingProps &
  DOMProps &
  FocusableProps &
  HoverEvents &
  PressEvents &
  BaseStyleProps &
  UnsafeStyleProps;

export type TextLinkAnchorProps = {
  /** Indicate that the link will open in a new tab. */
  isExternal?: boolean;
} & LinkDOMProps &
  CommonTextLinkProps;

export type TextLinkButtonProps = CommonTextLinkProps;

export type TextLinkProps = TextLinkAnchorProps | TextLinkButtonProps;

/**
 * Text links take users to another place in the application, and usually appear
 * within or directly following a sentence. Styled to resemble a hyperlink.
 *
 * [Taurus docs: `TextLink`](https://ui-sb.app.infinex.dev/?path=/docs/components-textlink--docs)
 */
export const TextLink = forwardRef(function TextLink(
  props: TextLinkProps,
  ref: ForwardedRef<HTMLAnchorElement | HTMLSpanElement>
) {
  const styleProps = useStyleProps({
    ...props,
    className: cn(variants({ prominence: props.prominence }), props.className),
  });

  if (isAnchorProps(props)) {
    return (
      <Link
        {...props}
        {...styleProps}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        target={props.isExternal ? '_blank' : props.target}
        rel={props.isExternal ? 'noopener noreferrer' : props.rel}
      >
        <Provider values={[[IconContext, iconContext]]}>
          {wrapChildren(props.children)}
          {props.isExternal && (
            <ArrowUpRightIcon aria-label=", opens in new tab" />
          )}
        </Provider>
      </Link>
    );
  }

  return (
    <TextLinkButton
      {...props}
      {...styleProps}
      ref={ref as ForwardedRef<HTMLSpanElement>}
    />
  );
});

// Would **much** prefer to use the `Button` component from
// "react-aria-components". All the shenanigans below are to fix a
// presentational limitation of the `<button/>` element, which refuses to
// display as `inline[-*]` no matter what CSS you throw at it.
const TextLinkButton = forwardRef(function TextLinkButton(
  props: TextLinkButtonProps,
  forwardedRef: ForwardedRef<HTMLSpanElement>
) {
  const { children, isDisabled, className, style } = props;
  const domRef = useObjectRef(forwardedRef);
  const { buttonProps, isPressed } = useButton(
    { elementType: 'span', ...props },
    domRef
  );
  const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);
  const { hoverProps, isHovered } = useHover(props);
  const state = { isDisabled, isPressed, isHovered, isFocused, isFocusVisible };

  return (
    <span
      {...mergeProps(buttonProps, focusProps, hoverProps)}
      {...toDataAttributes(state, { includeRacIdentifier: true })}
      ref={domRef}
      className={className}
      style={style}
    >
      <Provider values={[[IconContext, iconContext]]}>
        {wrapChildren(children)}
      </Provider>
    </span>
  );
});

// Utils
// -----------------------------------------------------------------------------

function isAnchorProps(props: TextLinkProps): props is TextLinkAnchorProps {
  return 'href' in props && props.href != null;
}

// Text nodes must be wrapped with an element to get the first/last child
// pseudo-elements to work around icons. Using `Text` for consistency with
// other content components but it isn't being targetted for styling (yet),
// just renders a simple span.
function wrapChildren(children: ReactNode) {
  return isReactText(children) ? <Text>{children}</Text> : children;
}

// Context
// -----------------------------------------------------------------------------

const iconContext = {
  // match the size of the text, but don't let it shrink smaller than
  // 1rem/16px to avoid weird SVG rendering artifacts.
  className: 'inline !size-[1em] min-h-4 min-w-4',
  // the word joiner character prevents the icon from wrapping onto its own
  // line, becoming an orphan. the CSS pseudo-elements approach is to avoid
  // mapping over children + interrogating the `node.type.displayName` or some
  // other silliness.
  render: (icon: ReactNode) => (
    <span
      data-char={UNICODE_CHARS.wordJoiner}
      className={cn(
        'relative inline text-nowrap',
        'last:ms-0.5 last:before:content-[attr(data-char)]',
        'first:me-0.5 first:after:content-[attr(data-char)]'
      )}
      style={{ top: '-0.05em' }}
    >
      {icon}
    </span>
  ),
};

// Variants
// -----------------------------------------------------------------------------

const variants = cva(
  `min-w-0 cursor-pointer wrap-break-word underline decoration-solid decoration-1 underline-offset-2 outline-hidden transition-all aria-disabled:cursor-auto aria-disabled:no-underline`,
  {
    variants: {
      prominence: {
        high: `hover:decoration-transparent focus-visible:decoration-double`,
        default: `decoration-[color-mix(in_srgb,currentColor_40%,transparent)] hover:decoration-[currentColor] focus-visible:decoration-[currentColor] focus-visible:decoration-double`,
        low: `decoration-transparent hover:brightness-150 focus-visible:decoration-[currentColor] focus-visible:decoration-double focus-visible:brightness-150`,
      },
    },
    defaultVariants: {
      prominence: 'default',
    },
  }
);
